import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { SavedSearchService, savedSearchKeys } from '../services/savedSearchService';

/**
 * Hook for fetching saved searches
 * @param {string} searchType - Type of search
 * @param {Object} options - React Query options
 */
export const useSavedSearches = (searchType, options = {}) => {
  return useQuery({
    queryKey: savedSearchKeys.list(searchType),
    queryFn: () => SavedSearchService.getSavedSearches(searchType),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Hook for creating a saved search
 */
export const useCreateSavedSearch = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => SavedSearchService.createSavedSearch(payload),
    onSuccess: (data, variables) => {
      // Invalidate the list for this search type
      queryClient.invalidateQueries({
        queryKey: savedSearchKeys.list(variables.search_type),
      });
      message.success(t('common|Search saved successfully'));
    },
    onError: () => {
      message.error(t('common|Failed to save search'));
    },
  });
};

/**
 * Hook for updating a saved search name
 */
export const useUpdateSavedSearch = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }) => SavedSearchService.updateSavedSearch(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedSearchKeys.all });
      message.success(t('common|Search updated successfully'));
    },
    onError: () => {
      message.error(t('common|Failed to update search'));
    },
  });
};

/**
 * Hook for deleting a saved search
 */
export const useDeleteSavedSearch = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => SavedSearchService.deleteSavedSearch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedSearchKeys.all });
      message.success(t('common|Search deleted'));
    },
    onError: () => {
      message.error(t('common|Failed to delete search'));
    },
  });
};

export default useSavedSearches;